import { buildApiUrl } from './apiConfig';
import { clearAuthStorage } from './auth';

const OVERLAY_ID = "password-change-logout-overlay";
const LOGIN_AFTER_PASSWORD_UPDATE = "/login?password_updated=true";
const LOGOUT_TIMEOUT_MS = 2000;

let inProgress = false;

export function isRequiredPasswordChangeLogoutInProgress(): boolean {
    return inProgress;
}

function fetchLogoutWithTimeout(): Promise<Response> {
    const controller = new AbortController();
    const logoutRequest = fetch(buildApiUrl("api/auth/logout"), {
        method: "POST",
        credentials: "include",
        headers: { Accept: "application/json" },
        signal: controller.signal,
    });
    const timeout = new Promise<never>((_, reject) => {
        window.setTimeout(() => {
            controller.abort();
            reject(new Error("logout timed out"));
        }, LOGOUT_TIMEOUT_MS);
    });
    return Promise.race([logoutRequest, timeout]);
}

/**
 * Blocks the UI immediately so the athlete cannot navigate (e.g. NCAA Tracker)
 * while the now-invalid session is being cleared.
 */
export function beginRequiredPasswordChangeLogout(): void {
    inProgress = true;
    if (typeof document === "undefined") {
        return;
    }
    if (document.getElementById(OVERLAY_ID)) {
        return;
    }
    const overlay = document.createElement("div");
    overlay.id = OVERLAY_ID;
    overlay.setAttribute("data-testid", OVERLAY_ID);
    overlay.setAttribute("role", "status");
    overlay.setAttribute("aria-live", "polite");
    overlay.textContent = "Password updated. Redirecting to sign in…";
    overlay.style.cssText = [
        "position:fixed",
        "inset:0",
        "z-index:10000",
        "display:flex",
        "align-items:center",
        "justify-content:center",
        "padding:1.5rem",
        "background:rgba(16,24,40,0.55)",
        "color:#ffffff",
        "font-size:1.05rem",
        "font-weight:700",
        "text-align:center",
    ].join(";");
    document.body.appendChild(overlay);
}

function redirectToPasswordUpdatedLogin(): void {
    try {
        clearAuthStorage();
    } catch {
        // Still leave the page even if local storage cleanup fails.
    }
    const target = LOGIN_AFTER_PASSWORD_UPDATE;
    try {
        if (typeof window.location.replace === "function") {
            window.location.replace(target);
            return;
        }
    } catch {
        // Fall through to href assignment.
    }
    window.location.href = target;
}

/**
 * Ends the current session after a password change so the user must sign in
 * again with the new password. The backend invalidates the JWT immediately
 * ({@code authTokenVersion} bump), so staying on the page would produce a
 * false "session expired" redirect on the next API call.
 */
export async function logoutAfterRequiredPasswordChange(): Promise<void> {
    beginRequiredPasswordChangeLogout();
    try {
        await fetchLogoutWithTimeout();
    } catch {
        // Best-effort — still clear client state and send the user to login.
    }

    redirectToPasswordUpdatedLogin();
}

/** Always log out after any successful password change, required or voluntary. */
export async function logoutAfterPasswordChange(): Promise<void> {
    try {
        localStorage.removeItem("requirePasswordChange");
    } catch {
        // Still continue logout if storage is unavailable.
    }
    await logoutAfterRequiredPasswordChange();
}

/** Test helper */
export function resetRequiredPasswordChangeLogoutForTests(): void {
    inProgress = false;
    document.getElementById(OVERLAY_ID)?.remove();
}
